import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useInviteProfessionalsToJobsMutation } from '~/domain/Jobs';
import {
  factoryGetEligibleCandidatesKey,
  selectInvitedCandidateIds,
  useGetEligibleCandidates,
} from '~/domain/PermanentJobs';
import { type IJob } from '~/models';
import { getJobDateInTimezone } from '~/utils/JobUtils';

/** The interview an invite goes to, and what scopes its candidate list. */
export interface IInterviewInviteTarget {
  postingId: string;
  interviewId: string;
  officeId?: string;
  /** `YYYY-MM-DD` in the office's timezone — drives candidate availability. */
  date?: string;
}

/** The invite target of an interview's job record (Interview Details). */
export const toInterviewInviteTarget = (job: IJob): IInterviewInviteTarget => ({
  postingId: job.postingId,
  interviewId: job.id,
  officeId: job.offer_owner?.id,
  date: getJobDateInTimezone(job),
});

interface IUseInterviewInviteOptions {
  /**
   * Runs after a successful send instead of showing the confirmation popup, for a
   * caller that closes its own surface first (Posting Details' invite dialog).
   */
  onInvited?: () => void;
}

/**
 * Drives the interview invite flow: tracks the selection + confirmation state, and
 * sends the selected candidates to this interview — invalidating the candidate query
 * on success so the just-invited rows flip to "Sent". Also surfaces how many
 * candidates are already invited. Kept as a hook so `InterviewInviteSection` stays
 * presentational and this is unit-testable.
 */
export const useInterviewInvite = (
  { postingId, interviewId, officeId, date }: IInterviewInviteTarget,
  { onInvited }: IUseInterviewInviteOptions = {},
) => {
  const queryClient = useQueryClient();

  const { mutate: inviteProfessionalsToJobs, isPending } = useInviteProfessionalsToJobsMutation();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showSuccess, setShowSuccess] = useState(false);
  // Remounting the list (via listKey) after a send resets its internal selection
  // and re-renders the freshly invited candidates as "Sent".
  const [listKey, setListKey] = useState(0);

  // Shares the list's query (same key → one fetch) to drive the invited count.
  const { data: candidates } = useGetEligibleCandidates(
    { postingId, date },
    { enabled: Boolean(postingId) },
  );
  const invitedCount = selectInvitedCandidateIds(candidates, interviewId).length;

  const invite = useCallback(() => {
    if (selectedIds.length === 0) return;

    inviteProfessionalsToJobs(
      { jobIds: [interviewId], professionalIds: selectedIds, officeId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: factoryGetEligibleCandidatesKey({ postingId, date }),
          });
          setSelectedIds([]);
          setListKey((key) => key + 1);
          if (onInvited) {
            onInvited();
          } else {
            setShowSuccess(true);
          }
        },
      },
    );
  }, [
    inviteProfessionalsToJobs,
    interviewId,
    officeId,
    postingId,
    date,
    selectedIds,
    queryClient,
    onInvited,
  ]);

  return {
    date,
    listKey,
    setSelectedIds,
    canInvite: selectedIds.length > 0,
    isInviting: isPending,
    invitedCount,
    showSuccess,
    closeSuccess: () => setShowSuccess(false),
    invite,
  };
};
